import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";

/**
 * Auto-scrolling review carousel. Pulls /reviews/featured then animates a marquee.
 * Duplicates the list twice so the CSS translate loop is seamless.
 */
export default function ReviewsMarquee() {
  const [reviews, setReviews] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/reviews/featured?limit=12").then(({ data }) => {
      if (!alive) return;
      setReviews(data.items || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const doubled = useMemo(() => [...reviews, ...reviews], [reviews]);

  if (!reviews.length) return null;

  const durationSec = Math.max(30, reviews.length * 6);

  return (
    <section className="bg-[#F4F1EA] border-y border-[#E5E2DC] overflow-hidden" data-testid="reviews-marquee-section">
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-semibold mb-2">Loved by the collective</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-[#1A1C1A]">Reviews that mean something</h2>
          </div>
          <Link
            to="/reviews"
            data-testid="reviews-view-all-link"
            className="hidden md:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-stone-600 hover:text-[#1A1C1A] pb-2"
          >
            View all reviews →
          </Link>
        </div>
      </div>

      <div className="relative pb-14" style={{ maskImage: "linear-gradient(90deg, transparent 0, black 5%, black 95%, transparent 100%)", WebkitMaskImage: "linear-gradient(90deg, transparent 0, black 5%, black 95%, transparent 100%)" }}>
        <div
          ref={trackRef}
          className="flex gap-5 animate-jc-marquee"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {doubled.map((r, i) => (
            <article
              key={`${r.id}-${i}`}
              data-testid="reviews-marquee-card"
              className="flex-shrink-0 w-[320px] md:w-[380px] bg-white border border-[#E5E2DC] rounded-2xl p-6 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[#1A1C1A] text-[#F9F9F6] flex items-center justify-center font-serif text-sm">
                  {r.author_initial || "?"}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A1C1A] truncate">{r.author_name}</p>
                  {r.verified_purchase && (
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#3F5F3F]">Verified purchase</p>
                  )}
                </div>
              </div>
              <StarRating value={r.rating} />
              {r.title && (
                <h3 className="mt-3 font-serif text-lg font-light text-[#1A1C1A] leading-snug">{r.title}</h3>
              )}
              <p className="mt-2 text-sm text-stone-600 line-clamp-4 leading-relaxed">{r.body}</p>
              {r.image_urls?.length > 0 && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  {r.image_urls.slice(0, 3).map((u, idx) => (
                    <img
                      key={idx}
                      src={u.startsWith("http") ? u : `${(process.env.REACT_APP_BACKEND_URL || "")}${u}`}
                      alt=""
                      className="w-16 h-16 object-cover rounded-md"
                    />
                  ))}
                </div>
              )}
              {r.product_name && (
                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-stone-400">on {r.product_name}</p>
              )}
            </article>
          ))}
        </div>
      </div>

      <div className="text-center pb-12 md:hidden">
        <Link
          to="/reviews"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-stone-600 hover:text-[#1A1C1A]"
        >
          View all reviews →
        </Link>
      </div>
    </section>
  );
}
